export const CLASS_PLAY_MAX_CLASS_LABEL_CHARS = 20;
export const CLASS_PLAY_MAX_PHRASE_CHARS = 30;
export const CLASS_PLAY_MAX_STORY_CHARS = 60;
export const CLASS_PLAY_MAX_FREE_TOPIC_CHARS = 20;

export const CLASS_PLAY_GAME_TYPES = [
  {
    id: 'balance',
    label: '밸런스 게임',
    default: true,
    exampleImage: '/images/events/class-play-balance.webp',
    exampleAlt: '3학년 2반 픽셀 밸런스 게임 PPT 표지 예시',
  },
  {
    id: 'choseong',
    label: '초성 퀴즈',
    exampleImage: '/images/events/class-play-choseong.webp',
    exampleAlt: '교실 TV용 픽셀 초성 퀴즈 PPT 표지 예시',
  },
  { id: 'ox', label: 'OX 퀴즈' },
  {
    id: 'golden_bell',
    label: '골든벨',
    exampleImage: '/images/events/class-play-golden-bell.webp',
    exampleAlt: '5학년 1반 픽셀 골든벨 PPT 표지 예시',
  },
];

export const CLASS_PLAY_EXAMPLES = CLASS_PLAY_GAME_TYPES.filter((game) => game.exampleImage);

export const CLASS_PLAY_CLASS_PRESETS = ['3학년 2반', '5학년 1반', '1학년 3반'];
export const CLASS_PLAY_DEFAULT_CLASS_LABEL = CLASS_PLAY_CLASS_PRESETS[0];

export const CLASS_PLAY_TOPICS = [
  { id: 'school_life', label: '학교생활·급식', default: true },
  { id: 'chuseok', label: '추석', activeUntil: '2026-09-26' },
  { id: 'hangeul', label: '한글날', activeUntil: '2026-10-09' },
  { id: 'free', label: '직접 입력' },
];

export const CLASS_PLAY_PIXEL_STYLE_DESCRIPTION = [
  '레트로 8비트 픽셀 게임 화면, 젭퀴즈처럼 귀엽고 장난기 있는 교실 게임 UI.',
  '하트·코인·별·두꺼운 픽셀 테두리, 또렷한 한글 픽셀 글씨.',
  '파스텔·수채화·교과서·시험지·무대조명·칠판 분필 스타일은 쓰지 마세요.',
].join(' ');

export const CLASS_PLAY_LOCKED_STYLE = {
  id: 'pixel',
  label: '픽셀 게임',
  theme: 'education_blue',
  styleDescription: CLASS_PLAY_PIXEL_STYLE_DESCRIPTION,
  default: true,
};

export const CLASS_PLAY_STYLES = [CLASS_PLAY_LOCKED_STYLE];

export const CLASS_PLAY_QUESTION_COUNTS = [
  { count: 1, slides: 5, default: true },
];

export const CLASS_PLAY_GRADES = ['초1-2', '초3-4', '초5-6', '중1', '중2', '중3'];
export const CLASS_PLAY_DEFAULT_GRADE = '초3-4';

const IMAGE_PPT_THEMES = new Set([
  'education_blue',
  'warm_orange',
  'nature_green',
  'elegant_purple',
  'clean_white',
  'calm_teal',
]);

const clampText = (value, maxChars) => (
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxChars)
);

export const normalizeClassPlayClassLabel = (value) => (
  clampText(value, CLASS_PLAY_MAX_CLASS_LABEL_CHARS)
);

export const normalizeClassPlayPhrase = (value) => (
  clampText(value, CLASS_PLAY_MAX_PHRASE_CHARS)
);

export const normalizeClassPlayStory = (value) => (
  clampText(value, CLASS_PLAY_MAX_STORY_CHARS)
);

export const normalizeClassPlayFreeTopic = (value) => (
  clampText(value, CLASS_PLAY_MAX_FREE_TOPIC_CHARS)
);

export const getDefaultClassPlayGameType = () => (
  CLASS_PLAY_GAME_TYPES.find((game) => game.default) || CLASS_PLAY_GAME_TYPES[0]
);

export const getDefaultClassPlayTopic = () => (
  CLASS_PLAY_TOPICS.find((topic) => topic.default) || CLASS_PLAY_TOPICS[0]
);

export const getDefaultClassPlayStyle = () => CLASS_PLAY_LOCKED_STYLE;

export const getDefaultClassPlayQuestionCount = () => (
  CLASS_PLAY_QUESTION_COUNTS.find((option) => option.default) || CLASS_PLAY_QUESTION_COUNTS[0]
);

export const getClassPlayGameType = (gameTypeId) => (
  CLASS_PLAY_GAME_TYPES.find((game) => game.id === gameTypeId)
  || getDefaultClassPlayGameType()
);

export const getClassPlayTopic = (topicId) => (
  CLASS_PLAY_TOPICS.find((topic) => topic.id === topicId)
  || getDefaultClassPlayTopic()
);

export const getClassPlayStyle = () => CLASS_PLAY_LOCKED_STYLE;

export const getClassPlayQuestionCount = (count) => (
  CLASS_PLAY_QUESTION_COUNTS.find((option) => option.count === Number(count))
  || getDefaultClassPlayQuestionCount()
);

export const resolveClassPlayTopicLabel = ({ topic, freeTopic } = {}) => {
  const selectedTopic = getClassPlayTopic(topic);
  if (selectedTopic.id !== 'free') return selectedTopic.label;
  return normalizeClassPlayFreeTopic(freeTopic) || getDefaultClassPlayTopic().label;
};

const GAME_INSTRUCTIONS = {
  balance: [
    '- 게임 유형: 밸런스 게임.',
    '- 문제 슬라이드는 two_column으로 "A vs B" 두 선택지를 큰 글씨·그림으로 보여주세요.',
    '- 각 문제 다음 슬라이드는 정답이 아니라 진행 안내입니다. quiz 레이아웃·정답·choices·answer_idx·점수는 넣지 마세요.',
    '- 진행 슬라이드는 content 또는 callout으로, 제목에 "손 들어보기!"를 넣고 본문에 "왜 골랐는지 한 명씩 말해보기"를 적어주세요.',
  ].join('\n'),
  choseong: [
    '- 게임 유형: 초성 퀴즈.',
    '- 문제 슬라이드 제목이나 본문에 초성 힌트를 한글 자음으로 크게 넣어주세요. 예: ㄱ ㅅ.',
    '- 정답 슬라이드에 정답을 보여주세요. 문제와 정답을 한 장씩 교차하세요.',
    '- 해당 학년 일상 단어만 내주세요. 대학교·전공 용어는 넣지 마세요.',
  ].join('\n'),
  ox: [
    '- 게임 유형: OX 퀴즈.',
    '- 논란 없는 확실한 재미 상식만 내주세요.',
    '- 문제 슬라이드와 정답 슬라이드를 한 장씩 교차하세요.',
    '- 정답 슬라이드에 정답과 한 줄 설명을 넣어주세요.',
  ].join('\n'),
  golden_bell: [
    '- 게임 유형: 골든벨.',
    '- 단답 문제를 쉬움에서 어려움 순서로 내주세요.',
    '- 문제 슬라이드와 정답 슬라이드를 한 장씩 교차하세요.',
    '- 정답 슬라이드에 정답과 한 줄 설명을 넣어주세요.',
  ].join('\n'),
};

const SAFETY_LINES = [
  '- 학생 개인 실명·가명·이니셜은 넣지 마세요.',
  '- 슬라이드에 학생 이름 기입란을 만들지 마세요. 반 이름만 제목으로 넣으세요.',
  '- 점수·등수 요소는 넣지 마세요.',
  '- 교과서 지문을 그대로 옮기지 마세요.',
  '- 사실 문제는 확실한 것만 내주세요.',
  '- 그림은 레트로 8비트 픽셀 게임(젭퀴즈) 느낌으로, 귀엽고 장난기 있게 그려주세요.',
  '- 비PRO 결과라면 하단에 작은 글씨로 whatquiz.co.kr을 넣어주세요.',
].join('\n');

export const buildClassPlayPrompt = ({
  classLabel,
  gameType,
  topic,
  freeTopic,
  phrase,
  classStory,
  questionCount,
} = {}) => {
  const selectedGame = getClassPlayGameType(gameType);
  const selectedCount = getClassPlayQuestionCount(questionCount);
  const topicLabel = resolveClassPlayTopicLabel({ topic, freeTopic });
  const safeClassLabel = normalizeClassPlayClassLabel(classLabel) || '우리 반';
  const safePhrase = normalizeClassPlayPhrase(phrase);
  const safeStory = normalizeClassPlayStory(classStory);
  const coverLine = safePhrase
    ? `표지 1장("${safeClassLabel} ${selectedGame.label}"을 크게, 응원 문구 "${safePhrase}" 포함)`
    : `표지 1장("${safeClassLabel} ${selectedGame.label}"을 크게)`;

  const lines = [
    `"${safeClassLabel}" 교실 TV로 진행하는 놀이 PPT를 만들어주세요. 수업 평가가 아니라 아이스브레이킹·레크리에이션용입니다.`,
    `- 구성: ${coverLine} → 진행 방법 1장 → 문제 ${selectedCount.count}개와 이어지는 1장 → 마무리 축하 1장. 총 ${selectedCount.slides}장. ${selectedCount.slides}장을 넘기지 마세요.`,
    GAME_INSTRUCTIONS[selectedGame.id],
    `- 주제: ${topicLabel}`,
  ];

  if (safeStory) {
    lines.push(`- 우리 반 이야기를 문제에 자연스럽게 녹여주세요: "${safeStory}"`);
  }
  lines.push(SAFETY_LINES);

  return lines.filter(Boolean).join('\n');
};

export const CLASS_PLAY_IMAGE_THEME_IDS = Array.from(IMAGE_PPT_THEMES);
